/*
 * Copyright (c) 2024-2025 Hardfin, Inc.
 */
import * as z from 'zod';
import {
  HubspotConfiguration,
  hubspotConfigurationResponseSchema,
} from '@/account/api/types';
import {
  UpdateHubspotPipelineItem,
  hubspotPipelineQueryKey,
  updateHubspotPipelines,
} from '@/account/api/update-hubspot-pipeline-config';
import { CRMConfigurationTab } from '@/account/components/crm-configuration-tab';
import { CRMResyncPipelineHubspot } from '@/account/components/crm-resync-hubspot';
import { api } from '@/api/axios-instance';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { parseResponse } from '@/api/parse-response';
import { FormDatePicker } from '@/components/form-date-picker';
import { LoadingIndicator } from '@/components/navigation-layout';
import { ScrollIndicator } from '@/components/scroll-indicator';
import {
  AddOutlined,
  DeleteRounded,
  HelpOutlineOutlined,
} from '@/constants/hardfin-standard-icons';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { useSupportError } from '@/hooks/use-support-error';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';

interface Props {
  onClose: () => void;
}

// Delay for 10 frames at 60hz to help with flicker
const FORM_FLICKER_DELAY_MS = 167;

const formSchema = z.object({
  rows: z.array(
    z.object({
      pipelineId: z
        .string({ required_error: 'A pipeline is required' })
        .min(1, 'A pipeline is required'),
      stageId: z
        .string({ required_error: 'A stage is required' })
        .min(1, 'A stage is required'),
      isEnabled: z.boolean(),
      shouldRemovePipeline: z.boolean().default(false),
      startDate: dateTimeSchema,
    })
  ),
});
type FormData = z.infer<typeof formSchema>;

/**
 * True when the pipeline fetch failed because the organization has no HubSpot
 * integration. That is a configuration state the tab renders, not a failure to
 * report, so the query resolves to null instead of throwing.
 */
function isHubspotNotConfiguredError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  return (
    handleAPIError(error).apiStatusCode ===
    ERROR_CODE_MAPPING.hubspot_not_configured
  );
}

export function CRMPipelineConfiguration(props: Props) {
  const [errorMessage, setErrorMessage] = useState('');
  const [isRefreshing, notifyRefresh] = useState(false);
  const { showSupportError, hideSupportError } = useSupportError();
  const [isFormDisabled, setFormDisabled] = useState(false);
  const formDisabledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const scrollIndicator = useScrollIndicator();
  /**
   * Handles click on scroll indicator.
   * Scrolls timeline to bottom smoothly.
   */
  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  const queryClient = useQueryClient();
  const setupDefaultValues = (
    configuration: HubspotConfiguration | null
  ): FormData => {
    if (
      configuration === null ||
      configuration.configuredPipelines.length == 0
    ) {
      return {
        rows: [
          {
            pipelineId: '',
            stageId: '',
            isEnabled: true,
            shouldRemovePipeline: false,
            startDate: DateTime.now().startOf('day'),
          },
        ],
      };
    }
    return {
      rows: [
        ...configuration.configuredPipelines.map((pipeline) => ({
          pipelineId: pipeline.pipelineId,
          stageId: pipeline.stageId,
          isEnabled: pipeline.isEnabled,
          shouldRemovePipeline: false,
          startDate: pipeline.startDate ?? DateTime.now().startOf('day'),
        })),
      ],
    };
  };

  // Fetch the current configuration
  const { data, fetchStatus, status } = useSuspenseQuery({
    queryKey: hubspotPipelineQueryKey,
    refetchOnWindowFocus: false, // Do-not refetch when window is refocused,
    queryFn: async () => {
      const url = '/v1/crm/hubspot/pipeline';
      try {
        const response = await api.get(url);
        const result = await parseResponse(
          hubspotConfigurationResponseSchema,
          response.data,
          { schemaName: 'HubspotConfiguration', endpoint: url }
        );
        // Return the pipeline data
        return result.data;
      } catch (error) {
        if (isHubspotNotConfiguredError(error)) {
          return null;
        }
        throw error;
      }
    },
  });

  // Setup the form
  const {
    formState,
    handleSubmit,
    reset,
    getFieldState,
    watch,
    control,
    clearErrors,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: setupDefaultValues(data),
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'rows',
  });

  // Memoize the default data so this doesn't cause re-rendering if nothing changes
  const defaultFormValues = useMemo(() => setupDefaultValues(data), [data]);

  // Reset the form when `data` changes (e.g. after invalidateQueries
  // refetches). Skip the initial mount: useForm({ defaultValues }) already
  // initialized the form, and an unconditional reset() here wipes
  // formState.isDirty if the effect re-fires for any reason.
  const previousDataRef = useRef(data);
  useEffect(() => {
    if (previousDataRef.current === data) {
      return;
    }
    previousDataRef.current = data;
    replace(defaultFormValues.rows);
    reset(defaultFormValues);
  }, [data, defaultFormValues, replace, reset]);

  // Clear the flicker-debounce timer on unmount so it cannot fire against a
  // torn-down component (this leaks as a "window is not defined" crash when a
  // test file finishes before the timer fires)
  useEffect(() => {
    return () => {
      if (formDisabledTimerRef.current !== null) {
        clearTimeout(formDisabledTimerRef.current);
      }
    };
  }, []);

  const updateHubspotPipelineConfigMutation = useMutation({
    mutationFn: (parameters: UpdateHubspotPipelineItem[]) => {
      return updateHubspotPipelines(parameters);
    },
    onSuccess: (data) => {
      const formData: FormData = {
        rows: [
          ...data.data.configuredPipelines.map((pipeline) => ({
            pipelineId: pipeline.pipelineId,
            stageId: pipeline.stageId,
            isEnabled: pipeline.isEnabled,
            shouldRemovePipeline: false,
            startDate: pipeline.startDate ?? DateTime.now().startOf('day'),
          })),
        ],
      };
      reset(formData);
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const safe = getUserFacingErrorMessage(apiError);
      // Reset both surfaces so a retry never leaves the prior global
      // support alert visible alongside the new inline error.
      setErrorMessage('');
      hideSupportError();
      if (safe === SUPPORT_FALLBACK_ERROR_MESSAGE) {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('Failed to update HubSpot pipeline configuration.');
      } else {
        setErrorMessage(safe);
      }
    },
  });

  const isLoading = isRefreshing || !(fetchStatus === 'idle');

  // Disable the ability to change data for any of the reasons
  const fieldsDisabled =
    isFormDisabled || // Block for refresh debounce
    isRefreshing || // Resync was clicked
    status == 'error' || // The query state is in error
    formState.isSubmitting || // The form is submitting
    !(fetchStatus === 'idle'); // Fetch is active

  /**
   * Handles form submission when the data is valid.
   * Only sends changed fields to the API to minimize unnecessary updates.
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    const parameters: UpdateHubspotPipelineItem[] = data.rows.map((record) => ({
      isEnabled: record.isEnabled,
      pipelineId: record.pipelineId,
      stageId: record.stageId,
      startDate: record.startDate,
      shouldRemovePipeline: record.shouldRemovePipeline,
    }));

    setFormDisabled(true);
    formDisabledTimerRef.current = setTimeout(() => {
      setFormDisabled(false);
    }, FORM_FLICKER_DELAY_MS);
    updateHubspotPipelineConfigMutation.mutate(parameters);
  };
  const formControlSx = {
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: '2fr 4fr',
    columnGap: 1,
    rowGap: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    marginTop: 2,
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  const formValues = watch('rows');
  const selectedPipelines = formValues.map((record) => record.pipelineId);
  if (isLoading) {
    return (
      <Stack
        alignContent="center"
        alignItems="center"
        justifyContent="center"
        sx={{ width: '100%' }}
      >
        <LoadingIndicator />
      </Stack>
    );
  }
  if (data === null) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        gap={1}
        sx={{ width: '100%', p: 4 }}
      >
        <Typography variant="subtitle1">HubSpot is not connected</Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          Connect HubSpot from the integrations list before choosing which deal
          stages sync into Hardfin
        </Typography>
      </Stack>
    );
  }
  return (
    <CRMConfigurationTab
      isDirty={formState.isDirty}
      onClose={props.onClose}
      errorMessage={errorMessage}
      onSubmit={handleSubmit(handleValidSubmission)}
      isDisabled={fieldsDisabled}
      tabBody={
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <ScrollIndicator
            label="More pipelines"
            isVisible={scrollIndicator.isVisible}
            onClick={handleClickScroll}
          />
          <Box
            ref={scrollIndicator.ref}
            overflow="auto"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                alignContent: 'center',
                pr: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Pipeline selection
              </Typography>
              <Box sx={{ marginLeft: 'auto', justifyContent: 'flex-end' }}>
                <CRMResyncPipelineHubspot
                  queryKey={hubspotPipelineQueryKey}
                  setErrorMessage={setErrorMessage}
                  notifyRefresh={notifyRefresh}
                  onRefresh={() => {
                    queryClient.invalidateQueries();
                  }}
                />
              </Box>
            </Box>
            <Typography
              variant="body2"
              color="textSecondary"
              align={'left'}
              marginTop={1}
              marginBottom={1}
              paddingRight={2}
            >
              Sync your HubSpot deal data into a new Hardfin project
              automatically when deals reach a certain stage
            </Typography>
            {fetchStatus !== 'idle' || isRefreshing ? (
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 'auto',
                  width: '100%',
                  flex: 1,
                  minHeight: 200, // Ensures there's enough vertical space
                }}
              >
                <LoadingIndicator />
              </Box>
            ) : (
              <Stack gap={2} sx={{ pr: 2 }}>
                {fields.map((field, idx) => {
                  const pipelineFieldState = getFieldState(
                    `rows.${idx}.pipelineId`
                  );
                  // Get all available pipelines, either it should be selected this row or not selected in another row
                  const availablePipelines = data.pipelines.filter(
                    (record) =>
                      !selectedPipelines.includes(record.id) ||
                      selectedPipelines[idx] === record.id
                  );
                  const stageFieldState = getFieldState(`rows.${idx}.stageId`);
                  const currentPipelineId = selectedPipelines[idx];
                  // Get the stage values from data.pipelines where the selectedPipeline equals the id
                  const activeStageOptions = data.pipelines
                    .filter((record) => currentPipelineId === record.id)
                    .flatMap((pipeline) => pipeline.stages);

                  return (
                    <Paper sx={{ p: 2 }} variant="outlined" key={field.id}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle1">
                          Pipeline {idx + 1}
                        </Typography>
                        {idx > 0 && (
                          // Allowing users to delete the first item can cause problems since the form defaults to adding a blank pipeline and "initializing it".
                          // It is complicated to allow people to delete the first pipeline, a "deactivate all pipelines" option should be created if that behavior is needed.
                          <IconButton>
                            <DeleteRounded
                              fontSize="small"
                              color="action"
                              onClick={() => {
                                remove(idx);
                              }}
                            />
                          </IconButton>
                        )}
                      </Stack>
                      <Box
                        sx={{
                          width: '100%',
                          margin: 'auto',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={formControlSx}>
                          <Typography variant="subtitle2" marginTop={0.5}>
                            Select pipeline
                          </Typography>
                          <Controller
                            control={control}
                            name={`rows.${idx}.pipelineId`}
                            render={(renderProps) => (
                              <Select
                                size="small"
                                fullWidth
                                disabled={fieldsDisabled}
                                error={pipelineFieldState.invalid}
                                placeholder="Select a pipeline"
                                value={renderProps.field.value}
                                onChange={(e) => {
                                  const pipelineId = e.target.value;
                                  const previousPipelineId =
                                    renderProps.field.value;
                                  renderProps.field.onChange(pipelineId);
                                  if (pipelineId !== previousPipelineId) {
                                    setValue(`rows.${idx}.stageId`, '', {
                                      shouldDirty: true,
                                    });
                                  }
                                  clearErrors(`rows.${idx}.pipelineId`);
                                }}
                              >
                                {data.pipelines.length == 0 && (
                                  <MenuItem key={''} value={''} disabled={true}>
                                    No pipelines available
                                  </MenuItem>
                                )}
                                {availablePipelines.map((pipeline) => (
                                  <MenuItem
                                    key={pipeline.id}
                                    value={pipeline.id}
                                  >
                                    {pipeline.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          {pipelineFieldState.invalid && (
                            <>
                              <Box></Box>
                              <FormHelperText error>
                                {pipelineFieldState.error?.message}
                              </FormHelperText>
                            </>
                          )}
                        </Box>
                        <Box sx={formControlSx}>
                          <Typography variant="subtitle2" marginTop={0.5}>
                            Select stage
                          </Typography>
                          <Controller
                            control={control}
                            name={`rows.${idx}.stageId`}
                            render={(renderProps) => (
                              <Select
                                error={stageFieldState.invalid}
                                size="small"
                                disabled={fieldsDisabled}
                                placeholder="Select a stage"
                                value={renderProps.field.value}
                                onChange={(e) => {
                                  renderProps.field.onChange(e.target.value);
                                  clearErrors(`rows.${idx}.stageId`);
                                }}
                              >
                                {activeStageOptions.length == 0 ? (
                                  <MenuItem key={''} value={''} disabled={true}>
                                    No stages available
                                  </MenuItem>
                                ) : (
                                  activeStageOptions.map((stage) => (
                                    <MenuItem key={stage.id} value={stage.id}>
                                      {stage.label}
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                            )}
                          />
                          {stageFieldState.invalid && <Box></Box>}
                          {stageFieldState.invalid && (
                            <FormHelperText error>
                              {stageFieldState.error?.message}
                            </FormHelperText>
                          )}
                        </Box>
                        <Box sx={formControlSx}>
                          <Typography variant="subtitle2" marginTop={0.5}>
                            Sync status
                          </Typography>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-end' }}
                          >
                            <Controller
                              control={control}
                              name={`rows.${idx}.isEnabled`}
                              render={(renderProps) => (
                                <ToggleButtonGroup
                                  exclusive
                                  value={renderProps.field.value}
                                  size="small"
                                  fullWidth
                                  color="standard"
                                  onChange={(_, value) => {
                                    if (value !== null) {
                                      renderProps.field.onChange(value);
                                    }
                                  }}
                                >
                                  <ToggleButton value={true} color="success">
                                    Active
                                  </ToggleButton>
                                  <ToggleButton value={false} color="warning">
                                    Paused
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              )}
                            />
                          </Box>
                        </Box>
                        <Box sx={formControlSx}>
                          <Stack alignItems="center" gap={0.5} direction="row">
                            <Typography variant="subtitle2" marginTop={0.5}>
                              Start date
                            </Typography>
                            <Tooltip
                              arrow
                              title={
                                'Hardfin will immediately import any deals modified after this date. Choose a date meaningful for your business, such as January 1. The sync will check for ongoing deal updates every 5 minutes.'
                              }
                            >
                              <HelpOutlineOutlined fontSize="small" />
                            </Tooltip>
                          </Stack>

                          <FormDatePicker
                            disableFuture
                            name={`rows.${idx}.startDate`}
                            control={control}
                            placeholderText={'Date'}
                            shortcuts={[
                              {
                                label: 'Today',
                                getValue: () => DateTime.now(),
                              },
                              {
                                label: '30 Days',
                                getValue: () => {
                                  return DateTime.now().minus({ days: 30 });
                                },
                              },
                              {
                                label: '90 Days',
                                getValue: () => {
                                  return DateTime.now().minus({ days: 90 });
                                },
                              },
                              {
                                label: '365 Days',
                                getValue: () => {
                                  return DateTime.now().minus({ days: 365 });
                                },
                              },
                            ]}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    mt: 2,
                  }}
                >
                  <Button
                    startIcon={<AddOutlined />}
                    variant="outlined"
                    onClick={() => {
                      append({
                        pipelineId: '',
                        stageId: '',
                        isEnabled: true,
                        shouldRemovePipeline: false,
                        startDate: DateTime.now().startOf('day'),
                      });
                    }}
                  >
                    Add another pipeline
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>
        </Box>
      }
    />
  );
}
